import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api, { CATEGORIES, categoryLabel, resolveImage } from "@/lib/api";
import { RichMultiline } from "@/lib/richText";
import Seo from "@/components/Seo";

const WRITINGS_DEFAULTS = {
  kicker: "The Collection",
  heading: "Writings & _words_",
  description:
    "Children's stories, poetry, and short articles — gathered here to encourage you in whatever season finds you today.",
  empty_message: "No writings here yet — come back soon.",
};

export default function Writings() {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeCategory = searchParams.get("category") || "all";
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copy, setCopy] = useState(WRITINGS_DEFAULTS);

  useEffect(() => {
    api
      .get("/pages")
      .then((r) => setCopy({ ...WRITINGS_DEFAULTS, ...(r.data?.writings || {}) }))
      .catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    const q = activeCategory === "all" ? "" : `?category=${activeCategory}`;
    api
      .get(`/posts${q}`)
      .then((r) => setPosts(r.data || []))
      .finally(() => setLoading(false));
  }, [activeCategory]);

  const setCat = (c) => {
    if (c === "all") setSearchParams({});
    else setSearchParams({ category: c });
  };

  return (
    <div className="bg-paper min-h-[80vh]" data-testid="writings-page">
      <Seo
        title="Writings"
        description={copy.description}
      />
      <section className="max-w-6xl mx-auto px-6 lg:px-10 pt-20 pb-12">
        <p className="text-xs uppercase tracking-[0.22em] text-terracotta mb-6 rise-in">
          {copy.kicker}
        </p>
        <h1 className="font-serif-display text-3xl lg:text-5xl tracking-tight text-ink leading-[1.02] rise-in rise-delay-1">
          <RichMultiline text={copy.heading} />
        </h1>
        <p className="mt-6 max-w-2xl text-ink-soft text-lg leading-relaxed rise-in rise-delay-2">
          {copy.description}
        </p>

        {/* Category filter */}
        <div className="mt-12 flex flex-wrap gap-2 border-b border-rule pb-6">
          <button
            onClick={() => setCat("all")}
            data-testid="filter-all"
            className={`px-4 py-2 text-xs uppercase tracking-[0.18em] border transition-colors ${
              activeCategory === "all"
                ? "bg-ink text-paper border-ink"
                : "border-rule text-ink-soft hover:border-ink hover:text-ink"
            }`}
          >
            All
          </button>
          {CATEGORIES.map((c) => (
            <button
              key={c.key}
              onClick={() => setCat(c.key)}
              data-testid={`filter-${c.key}`}
              className={`px-4 py-2 text-xs uppercase tracking-[0.18em] border transition-colors ${
                activeCategory === c.key
                  ? "bg-ink text-paper border-ink"
                  : "border-rule text-ink-soft hover:border-ink hover:text-ink"
              }`}
            >
              {c.label}
            </button>
          ))}
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-6 lg:px-10 pb-24">
        {loading ? (
          <p className="text-ink-soft" data-testid="writings-loading">Loading…</p>
        ) : posts.length === 0 ? (
          <div className="py-20 text-center" data-testid="writings-empty">
            <p className="font-serif-display text-2xl text-ink-soft italic">
              {copy.empty_message}
            </p>
          </div>
        ) : (
          <ul className="divide-y divide-rule" data-testid="writings-list">
            {posts.map((p, idx) => (
              <li key={p.id} className="py-10 group">
                <Link
                  to={`/writings/${p.slug}`}
                  data-testid={`post-link-${idx}`}
                  className="grid grid-cols-1 md:grid-cols-12 gap-8 items-start"
                >
                  <div className="md:col-span-3">
                    {p.cover_image ? (
                      <img
                        src={resolveImage(p.cover_image)}
                        alt={p.title}
                        className="w-full h-44 object-cover"
                      />
                    ) : (
                      <div className="w-full h-44 bg-paper-2 border border-rule flex items-center justify-center">
                        <span className="font-serif-display text-3xl text-ink-soft italic">
                          B.R.
                        </span>
                      </div>
                    )}
                  </div>
                  <div className="md:col-span-9">
                    <p className="text-xs uppercase tracking-[0.2em] text-sage mb-3">
                      {categoryLabel(p.category)}
                      {p.published_at && (
                        <span className="ml-3 text-ink-soft normal-case tracking-normal">
                          · {new Date(p.published_at).toLocaleDateString(undefined, {
                            year: "numeric",
                            month: "long",
                            day: "numeric",
                          })}
                        </span>
                      )}
                    </p>
                    <h2 className="font-serif-display text-3xl lg:text-4xl text-ink leading-tight tracking-tight group-hover:text-terracotta transition-colors">
                      {p.title}
                    </h2>
                    {p.excerpt && (
                      <p className="mt-4 text-ink-soft max-w-2xl leading-relaxed">
                        {p.excerpt}
                      </p>
                    )}
                    <span className="mt-5 inline-block text-xs uppercase tracking-[0.18em] text-terracotta link-underline">
                      Continue reading
                    </span>
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
